import React, { useState, useEffect } from 'react';
import { fetchLatestQueries } from '../api';
import BiModuleSection from '../components/BiModuleSection';
import Spinner from '../components/Spinner';

function groupByCategory(queries) {
  const map = {};
  for (const q of queries) {
    if (!map[q.category]) map[q.category] = [];
    map[q.category].push(q);
  }
  return Object.keys(map).sort().map(cat => ({ category: cat, queries: map[cat] }));
}

function downloadJson(queries) {
  const grouped = {};
  for (const q of queries) {
    const cat = q.category;
    const sub = q.subcategory || '';
    if (!grouped[cat]) grouped[cat] = {};
    if (!grouped[cat][sub]) grouped[cat][sub] = [];
    grouped[cat][sub].push({
      name:    q.queryName,
      premium: q.isPremium,
      query:   q.isPremium ? '' : q.sql,
    });
  }
  const blob = new Blob([JSON.stringify(grouped, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'prebuilt_queries.json';
  a.click();
  URL.revokeObjectURL(url);
}

export default function BiConnector() {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    fetchLatestQueries()
      .then(setQueries)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const groups = groupByCategory(queries);

  return (
    <div className="p-8 w-full">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-100 tracking-tight">BI Connector</h1>
            <p className="text-xs mt-0.5" style={{ color: '#4b5563' }}>
              Showing latest version of each query
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {!loading && !error && (
            <span className="text-xs" style={{ color: '#6b7280' }}>
              <span className="font-semibold text-gray-300">{queries.length}</span> queries
            </span>
          )}
          {!loading && !error && queries.length > 0 && (
            <button
              onClick={() => downloadJson(queries)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
              style={{ backgroundColor: '#1e2130', color: '#9ca3af', border: '1px solid #2d3148' }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#2d3148'; e.currentTarget.style.color = '#e4e6f0'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#1e2130'; e.currentTarget.style.color = '#9ca3af'; }}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download JSON
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-xl px-4 py-3 mb-5"
          style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#f87171' }} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p className="text-sm" style={{ color: '#f87171' }}>{error}</p>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center gap-2 py-16 justify-center" style={{ color: '#4b5563' }}>
          <Spinner className="w-5 h-5" />
          <span className="text-sm">Loading…</span>
        </div>
      ) : groups.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-2xl"
          style={{ backgroundColor: '#14161e', border: '1px solid #1e2130' }}>
          <p className="text-sm font-medium" style={{ color: '#374151' }}>No queries found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map(({ category, queries: cq }) => (
            <BiModuleSection key={category} category={category} queries={cq} />
          ))}
        </div>
      )}
    </div>
  );
}
